import Ellipsis from "@/Components/Base/Ellipsis";
import { IconsVariant } from "@/Components/Icon/Icon";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import useOrderInformationHook from "@/shared/hooks/useOrderInformationHook";
import { deliveryMethods } from "@/shared/utils/constant";
import { DeliveryMethods } from "@/shared/utils/enum";
import useOrderHelper from "@/shared/v2/hooks/useOrderHelper";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useTranslation } from "react-i18next";
import {
  formatDate,
  formatPrice,
  getFullAddressV2,
} from "../../shared/utils/helpers";
import Whatsapp from "../Base/Whatsapp";
import IconToolTip from "../Icon/IconToolTip";
import ReplyViaEmail from "../ReplyViaEmail";
import ClientDiscountContent from "./ClientDiscountContent";
import Tooltip from "@/Components/Tooltip/Tooltip";
import { cn } from "@/shared/utils/cn";
import usePaymentHook from "@/shared/hooks/usePaymentHook";
import useSourceHook from "@/shared/hooks/useSourceHook";

interface DataProps {
  value: any;
  label: string | React.ReactNode;
  highlight?: boolean;
  borderBottom?: boolean;
  icon?: IconsVariant;
}

const Data = ({ label, value, highlight, borderBottom = true }: DataProps) => {
  const isReactNode = typeof label !== "string";

  return (
    <div
      className={cn(
        "flex items-center gap-4 p-2 text-2xl md:text-3xl",
        highlight ? "bg-light" : "",
        borderBottom ? "border-b border-border" : ""
      )}
    >
      <div className="w-[45%] font-semibold">
        {isReactNode ? label : `${label}: `}
      </div>
      <div className="items-center w-[55%]">{value}</div>
    </div>
  );
};

const IconData = ({ label, value, icon }: DataProps) => {
  return (
    <div className="flex items-center p-2">
      <div style={{ fontWeight: "bold" }}>{`${label}: `}</div>
      <div className="flex items-center">
        {icon && (
          <IconToolTip
            text={`${value}`}
            variants={icon}
            className="fill-text lg:size-12"
          />
        )}
      </div>
    </div>
  );
};

interface Props {
  order: OrderV2;
  showTipsOnLive?: boolean;
}

const Client = ({ order, showTipsOnLive }: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobileHook();
  const {
    isWebsite,
    isPos,
    payment_method_id,
    source: _source,
    delivery_method_id,
    total_paid,
    total_price,
    is_custom_time_slot_selected,
    custom_time_slot_label,
    hand_over_time,
    subtotal_prices,
    tip,
  } = useOrderInformationHook(order);

  const {
    name,
    first_name,
    last_name,
    telephone,
    email,
    user_note,
    website_account_id,
  } = useOrderHelper(order);
  const { getPayment } = usePaymentHook();
  const { getSource } = useSourceHook();
  const paymentMethod = getPayment(payment_method_id);
  const source = getSource(_source);
  const delivery = deliveryMethods.find((d) => d.value === delivery_method_id);
  const totalPrice = parseFloat(total_price || "0");
  const totalPaid = parseFloat(total_paid || "0");
  const discountAmount = parseFloat(subtotal_prices["2"] || "0");

  return (
    <>
      <div className="p-4 mb-12">
        <div
          className="flex justify-center"
          style={{
            gap: isMobile ? "0.5rem" : "5rem",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <IconData
            label={t("orders_live.source")}
            value={t(`${source?.translationKey}`)}
            icon={source?.icon}
          />

          <IconData
            label={t("orders_live.deliveryMethod")}
            value={t(`${delivery?.translationKey}`)}
            icon={delivery?.icon}
          />

          <IconData
            label={t("orders_live.paymentMethod")}
            value={t(`${paymentMethod?.translationKey}`)}
            icon={paymentMethod?.icon}
          />
        </div>

        <div className="mt-8 flex flex-col md:flex-row w-full gap-[0.4rem]">
          <div className="flex flex-col border border-border p-2 rounded-lg w-full md:w-1/2">
            <div className="text-3xl font-semibold text-white bg-dark p-4 -m-2 mb-2 rounded-t-lg">
              {t("orders_live.clientInfo")}
            </div>
            <Data
              label={t("orders_live.name")}
              value={
                name
                  ? name
                  : first_name === undefined
                    ? "-"
                    : `${first_name} ${last_name}`
              }
            />

            <Data
              label={t("orders_live.address")}
              value={
                delivery_method_id === DeliveryMethods.DELIVERY
                  ? getFullAddressV2(order)
                  : "-"
              }
            />

            <Data
              label={t("orders_live.telephone")}
              value={
                <div className="flex items-center gap-4">
                  <div> {telephone}</div>
                  {(isWebsite || isPos) && telephone && telephone !== "" && (
                    <Whatsapp phone={telephone} />
                  )}
                </div>
              }
            />

            <Data
              label={t("orders_live.email")}
              value={
                <div className="flex items-center gap-4">
                  <Tooltip content={email || ""}>
                    <div>
                      <div className="max-w-[75%]">
                        <Ellipsis>{email || ""}</Ellipsis>
                      </div>
                    </div>
                  </Tooltip>
                  {(isWebsite || isPos) && email && (
                    <ReplyViaEmail order={order} />
                  )}
                </div>
              }
            />

            <Data label={t("orders_live.note")} value={user_note || "-"} />

            <Data
              label={t("orders_live.websiteAccountId")}
              value={website_account_id || "-"}
              borderBottom={false}
            />
          </div>

          <div
            className="flex flex-col border border-border p-2 rounded-lg"
            style={{ flex: isMobile ? "1 0 100%" : "1 0 50%" }}
          >
            <div className="text-3xl font-semibold text-white bg-dark p-4 -m-2 mb-2 rounded-t-lg">
              {t("orders_live.orderInfo")}
            </div>

            <Data
              label={t("orders_live.subtotal")}
              value={formatPrice(subtotal_prices["1"])}
            />

            <Data
              label={
                <div className="flex items-end gap-4">
                  {t("orders_live.discount")}
                  {discountAmount !== 0 && (
                    <ClientDiscountContent cartContent={order.cart_content} />
                  )}
                </div>
              }
              value={formatPrice(subtotal_prices["2"] || "0")}
            />

            <Data
              label={t("orders_live.deliveryCost")}
              value={formatPrice(subtotal_prices["3"] || "0")}
            />

            <Data
              label={t("orders_live.serviceCost")}
              value={formatPrice(subtotal_prices["4"] || "0")}
            />

            <Data
              label={t("orders_live.extraCost")}
              value={formatPrice(subtotal_prices["6"] || "0")}
            />

            {showTipsOnLive && (
              <Data
                label={t("orders_live.tip")}
                highlight={
                  parseFloat(tip) > 0 || parseFloat(subtotal_prices["5"]) > 0
                }
                value={formatPrice(tip ? tip : subtotal_prices["5"])}
              />
            )}
            <Data
              label={t("orders_live.total")}
              value={`${formatPrice(totalPrice)} ${
                totalPaid
                  ? totalPaid > totalPrice
                    ? `( ${t("orders_live.customerIsOwed", {
                        value: formatPrice(totalPaid - totalPrice),
                      })} )`
                    : `( ${t("orders_live.customerNeedsToPay", {
                        value: formatPrice(totalPrice - totalPaid),
                      })} )`
                  : ""
              } `}
            />

            <Data
              label={t("orders_live.date")}
              value={
                is_custom_time_slot_selected
                  ? custom_time_slot_label
                  : formatDate(hand_over_time, "dateTime")
              }
              borderBottom={false}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Client;
